import { createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { Button } from '@televet/kibble-ui/Button';
import { Card } from '@televet/kibble-ui/Card';
import { Select } from '@televet/kibble-ui/Select';
import { Tag } from '@televet/kibble-ui/Tag';
import { Text } from '@televet/kibble-ui/Text';
import { TextInput } from '@televet/kibble-ui/TextInput';
import { Box, Flex, Table } from '@televet/kibble-ui/chakra';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { DeleteDialog } from '@/components/DeleteDialog';
import { PageContainer } from '@/components/PageContainer';
import { toaster } from '@/components/ui/toaster';
import { trpc } from '@/lib/trpc';

type Category = 'preference' | 'clinicProfile' | 'terminology';
type Status = 'active' | 'archived';

const CATEGORY_OPTIONS = [
  { value: '', label: 'All categories' },
  { value: 'preference', label: 'Preference' },
  { value: 'clinicProfile', label: 'Clinic Profile' },
  { value: 'terminology', label: 'Terminology' },
];

const STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: 'active', label: 'Active' },
  { value: 'archived', label: 'Archived' },
];

type MemoryRow = {
  id: string;
  clinicId: string | null;
  userId: string | null;
  content: string;
  category: string;
  status: string;
  createdByUser: { id: string; email: string } | null;
  createdAt: string | Date;
};

/**
 * Scope label for a memory row (see AgentMemory model):
 *   clinic set, user null -> shared across the clinic
 *   clinic set, user set  -> the user at that clinic
 *   clinic null, user set -> the user across all clinics
 */
function scopeLabel(memory: MemoryRow): string {
  if (memory.clinicId && !memory.userId) return 'Clinic';
  if (memory.clinicId && memory.userId) return 'User @ clinic';
  return 'User (global)';
}

export const Route = createFileRoute('/_authenticated/agent-memory/')({
  beforeLoad: () => ({ pageTitle: 'Agent Memory' }),
  loader: () => ({ pageTitle: 'Agent Memory' }),
  component: AgentMemory,
});

function AgentMemory() {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [clinicIdFilter, setClinicIdFilter] = useState('');
  const [userIdFilter, setUserIdFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<Category | ''>('');
  const [statusFilter, setStatusFilter] = useState<Status | ''>('');
  const [archiveId, setArchiveId] = useState<string | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const listQueryInput = useMemo(
    () => ({
      page,
      limit: 15,
      where: {
        ...(clinicIdFilter ? { clinicId: clinicIdFilter } : {}),
        ...(userIdFilter ? { userId: userIdFilter } : {}),
        ...(categoryFilter ? { category: categoryFilter as Category } : {}),
        ...(statusFilter ? { status: statusFilter as Status } : {}),
      },
    }),
    [page, clinicIdFilter, userIdFilter, categoryFilter, statusFilter]
  );

  const { data, isLoading } = useQuery(trpc.agentMemory.list.queryOptions(listQueryInput));

  const invalidateList = () =>
    queryClient.invalidateQueries({ queryKey: [['agentMemory', 'list']] });

  const { mutate: archiveMemory, isPending: isArchiving } = useMutation(
    trpc.agentMemory.archive.mutationOptions({
      onSuccess: () => {
        toaster.success({
          title: 'Memory archived',
          description: 'It will no longer be injected into the agent.',
        });
        invalidateList();
        setArchiveId(null);
      },
      onError: (error) => {
        toaster.error({
          title: 'Failed to archive memory',
          description: error.message || 'Failed to archive memory',
          duration: 10000,
          closable: true,
        });
      },
    })
  );

  const { mutate: deleteMemory, isPending: isDeleting } = useMutation(
    trpc.agentMemory.deleteOne.mutationOptions({
      onSuccess: () => {
        toaster.success({
          title: 'Memory deleted',
          description: 'The memory was permanently removed.',
        });
        invalidateList();
        setDeleteId(null);
      },
      onError: (error) => {
        toaster.error({
          title: 'Failed to delete memory',
          description: error.message || 'Failed to delete memory',
          duration: 10000,
          closable: true,
        });
      },
    })
  );

  return (
    <PageContainer>
      <Flex direction="column" gap={4} p={8} width="100%">
        <Flex gap={3} align="center" wrap="wrap">
          <TextInput
            placeholder="Filter by clinicId..."
            value={clinicIdFilter}
            onChange={(e) => {
              setClinicIdFilter(e.target.value);
              setPage(1);
            }}
            maxW="260px"
          />
          <TextInput
            placeholder="Filter by userId..."
            value={userIdFilter}
            onChange={(e) => {
              setUserIdFilter(e.target.value);
              setPage(1);
            }}
            maxW="260px"
          />
          <Select
            options={CATEGORY_OPTIONS}
            value={[categoryFilter]}
            onValueChange={(details) => {
              setCategoryFilter((details.value[0] as Category | '') ?? '');
              setPage(1);
            }}
            placeholder="Category"
          />
          <Select
            options={STATUS_OPTIONS}
            value={[statusFilter]}
            onValueChange={(details) => {
              setStatusFilter((details.value[0] as Status | '') ?? '');
              setPage(1);
            }}
            placeholder="Status"
          />
        </Flex>

        <Flex flex="1 1 auto" justify="center" align="flex-start">
          <Card
            flex="1 1 auto"
            direction="column"
            borderColor="border.default"
            rounded="lg"
            overflow="hidden"
          >
            <Box overflowX="auto" position="relative" width="100%">
              <Table.Root size="md" width="100%" minWidth="960px">
                <Table.Header>
                  <Table.Row>
                    <Table.ColumnHeader minWidth="170px" whiteSpace="nowrap">
                      Created
                    </Table.ColumnHeader>
                    <Table.ColumnHeader minWidth="120px" whiteSpace="nowrap">
                      Scope
                    </Table.ColumnHeader>
                    <Table.ColumnHeader minWidth="130px" whiteSpace="nowrap">
                      Category
                    </Table.ColumnHeader>
                    <Table.ColumnHeader minWidth="100px" whiteSpace="nowrap">
                      Status
                    </Table.ColumnHeader>
                    <Table.ColumnHeader minWidth="320px">Content</Table.ColumnHeader>
                    <Table.ColumnHeader minWidth="200px" whiteSpace="nowrap">
                      Author
                    </Table.ColumnHeader>
                    <Table.ColumnHeader
                      textAlign="end"
                      width="160px"
                      minWidth="160px"
                      position="sticky"
                      right="0"
                      bg="bg"
                      zIndex={1}
                      borderLeft="1px solid"
                      borderColor="border.default"
                      whiteSpace="nowrap"
                    >
                      Actions
                    </Table.ColumnHeader>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {isLoading && (
                    <Table.Row>
                      <Table.Cell colSpan={7}>
                        <Text color="text.subtle">Loading…</Text>
                      </Table.Cell>
                    </Table.Row>
                  )}
                  {!isLoading && data?.memories.length === 0 && (
                    <Table.Row>
                      <Table.Cell colSpan={7}>
                        <Text color="text.subtle">No memories match the current filters.</Text>
                      </Table.Cell>
                    </Table.Row>
                  )}
                  {data?.memories.map((memory) => (
                    <Table.Row key={memory.id}>
                      <Table.Cell whiteSpace="nowrap">
                        {new Date(memory.createdAt).toLocaleString()}
                      </Table.Cell>
                      <Table.Cell whiteSpace="nowrap">
                        <Flex direction="column" gap={0}>
                          <Text fontSize="sm">{scopeLabel(memory)}</Text>
                          <Text fontSize="xs" color="text.subtle">
                            {memory.clinicId ? `clinic ${memory.clinicId}` : ''}
                            {memory.clinicId && memory.userId ? ' · ' : ''}
                            {memory.userId ? `user ${memory.userId}` : ''}
                          </Text>
                        </Flex>
                      </Table.Cell>
                      <Table.Cell whiteSpace="nowrap">
                        <Tag label={memory.category} variant="default" />
                      </Table.Cell>
                      <Table.Cell whiteSpace="nowrap">
                        <Tag
                          label={memory.status}
                          variant={memory.status === 'active' ? 'info' : 'default'}
                        />
                      </Table.Cell>
                      <Table.Cell maxW="440px">
                        <Text fontSize="sm">{memory.content}</Text>
                      </Table.Cell>
                      <Table.Cell whiteSpace="nowrap">
                        <Text color={memory.createdByUser ? undefined : 'text.subtle'}>
                          {memory.createdByUser?.email ?? '—'}
                        </Text>
                      </Table.Cell>
                      <Table.Cell
                        textAlign="end"
                        position="sticky"
                        right="0"
                        bg="bg"
                        zIndex={1}
                        borderLeft="1px solid"
                        borderColor="border.default"
                      >
                        <Flex gap={2} justify="flex-end">
                          {memory.status === 'active' && (
                            <Button
                              size="sm"
                              variant="secondary"
                              onClick={() => setArchiveId(memory.id)}
                            >
                              Archive
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="ghostDestructive"
                            iconName="trashcan"
                            onClick={() => setDeleteId(memory.id)}
                          >
                            Delete
                          </Button>
                        </Flex>
                      </Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table.Root>
            </Box>
          </Card>
        </Flex>

        {data?.pagination?.total ? (
          <Flex justify="space-between" align="center" gap={4} wrap="wrap" px={2}>
            <Text fontSize="sm" variant="subtle">
              {data.pagination.total} memor
              {data.pagination.total !== 1 ? 'ies' : 'y'} total
            </Text>
            <Flex gap={3} align="center">
              <Button
                size="sm"
                disabled={!data.pagination.hasPrev}
                onClick={() => setPage(page - 1)}
              >
                Previous
              </Button>
              <Text fontSize="sm" variant="subtle" minWidth="fit-content">
                Page {data.pagination.page} of {data.pagination.totalPages}
              </Text>
              <Button
                size="sm"
                disabled={!data.pagination.hasNext}
                onClick={() => setPage(page + 1)}
              >
                Next
              </Button>
            </Flex>
          </Flex>
        ) : undefined}
      </Flex>

      <ConfirmDialog
        isOpen={!!archiveId}
        title="Archive Memory"
        description="Archiving stops this memory from being injected into the agent going forward. It stays in the record and can be restored."
        confirmLabel="Archive"
        onConfirm={() => archiveId && archiveMemory({ id: archiveId })}
        onCancel={() => setArchiveId(null)}
        isLoading={isArchiving}
      />

      <DeleteDialog
        isOpen={!!deleteId}
        title="Delete Memory"
        description="Are you sure you want to permanently delete this memory? This cannot be undone. Prefer Archive unless the row is genuinely bad (e.g. accidental PII)."
        onConfirm={() => deleteId && deleteMemory({ id: deleteId })}
        onCancel={() => setDeleteId(null)}
        isLoading={isDeleting}
        isDangerous
      />
    </PageContainer>
  );
}
